import { Link, NavLink } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useAuthModal } from '../../hooks/authModalContext'
import { NotificationBell } from './NotificationBell'
import { RoleSwitcherButton } from './RoleSwitcher'
import { getDiscoveryTabs } from './discoveryTabs'
import { HeaderDiscoverySearch } from './DiscoverySearchContext'
import { getVerticalMeta, useVertical } from './useVertical'
import { SwitchServiceButton } from './SwitchServiceButton'
import { VerticalBrand } from './VerticalBrand'

export function DiscoveryTabs({ className = '' }: { className?: string }) {
  const { activeVertical } = useVertical()
  const tabs = getDiscoveryTabs(activeVertical)
  return (
    <nav
      className={`flex items-center gap-1 overflow-x-auto rounded-xl border border-stone-200 bg-stone-50 p-1 ${className}`}
    >
      {tabs.map((tab) => {
        const Icon = tab.icon
        return (
          <NavLink
            key={tab.path}
            to={tab.path}
            end
            aria-label={tab.label}
            className={({ isActive }) =>
              `flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold tracking-wide transition sm:flex-none sm:justify-start ${
                isActive ? 'bg-brand-700 text-white shadow' : 'text-stone-700 hover:bg-white'
              }`
            }
          >
            <Icon className="h-5 w-5 sm:h-4 sm:w-4" aria-hidden="true" />
            <span className="hidden sm:inline">{tab.label}</span>
          </NavLink>
        )
      })}
    </nav>
  )
}

export function PublicHeader() {
  const { isAuthenticated, logout } = useAuth()
  const { openAuthModal } = useAuthModal()
  const { activeVertical } = useVertical()
  const verticalMeta = getVerticalMeta(activeVertical)
  // Brand mark follows the active vertical (VerticalBrand reads the context),
  // matching the launcher tile and the signed-in header. This frame also wraps
  // homestay/thrift *detail* pages, so a fixed mark would show the wrong vertical.

  return (
    <header className="sticky top-0 z-30 border-b border-stone-200 bg-white/95 backdrop-blur">
      {/* Full-bleed like the signed-in header (AppShell), so the brand sits at the
          same offset whether or not you are logged in. Page content below keeps
          its own max-width. */}
      <div className="grid w-full grid-cols-[auto_auto] items-center gap-3 px-4 py-3 md:grid-cols-[auto_minmax(0,1fr)_auto] md:px-6">
        <Link
          to={verticalMeta.home}
          title={`${verticalMeta.label} home`}
          aria-label={`${verticalMeta.label} home`}
          className="flex items-center gap-3 text-stone-950"
        >
          <VerticalBrand />
        </Link>
        <div className="col-span-2 row-start-2 flex min-w-0 flex-col items-center gap-2.5 md:col-span-1 md:row-start-auto">
          <DiscoveryTabs className="max-w-full" />
          <HeaderDiscoverySearch />
        </div>
        {isAuthenticated ? (
          <div className="flex items-center justify-end gap-3">
            <div className="hidden sm:block">
              <RoleSwitcherButton />
            </div>
            <SwitchServiceButton />
            <NotificationBell />
            <button
              onClick={logout}
              className="hidden items-center gap-2 rounded-xl border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-100 sm:inline-flex"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-3">
            <SwitchServiceButton />

            <button
              type="button"
              onClick={openAuthModal}
              className="rounded-xl border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-100"
            >
              Sign in
            </button>
          </div>
        )}
      </div>
    </header>
  )
}
